import { ICON_LLMARK } from './icons.js';
import type { TranslateFn } from './i18n.js';

/**
 * Tiny floating bubble that anchors to the user's selection while the
 * widget is fetching audio. Sits ABOVE the selection so it doesn't
 * cover the text being read. Fades out the instant audio starts.
 *
 * Visible content is the animated LL brand mark — same wordmark the pill
 * shows when active — so the loading state reads as 'Llais is working' at
 * a glance, not as a different mid-flight glyph. The status label
 * ('Speaking…' / 'Translating…' / 'Yn siarad…' etc.) is kept in the DOM
 * but hidden visually via CSS so screen readers still announce it via
 * aria-live="polite" without crowding the sighted UI.
 *
 * Carries a translator reference so the SR-only label respects the
 * UI language toggle.
 */
export class SelectionBubble {
  private el: HTMLDivElement;
  private tr: TranslateFn;
  private lastKey: 'speak' | 'translate' | null = null;

  private readingTextNode: Text | null = null;

  constructor(tr: TranslateFn) {
    this.tr = tr;
    this.el = document.createElement('div');
    this.el.className = 'bubble';
    this.el.setAttribute('role', 'status');
    this.el.setAttribute('aria-live', 'polite');
    // Icon is always the animated LL brand mark; the label is set per-
    // call and SR-only so screen reader users still get the speak/
    // translate distinction even though sighted users see only the mark.
    // The .bubble-reading slot stays empty + display:none until
    // showReading() populates it for translated-audio playback.
    this.el.innerHTML =
      `<span class="bubble-icon">${ICON_LLMARK}</span>` +
      `<span class="bubble-label"></span>` +
      `<span class="bubble-reading"></span>`;
  }

  get root(): HTMLElement {
    return this.el;
  }

  show(label: string): void {
    // Reset reading state before dropping back into the loading-icon
    // chip. Without this, a new selection started while a translated
    // playback's bubble was still visible would reposition the card
    // but keep showing the previous translated text + hidden icon
    // (Codex on PR #55). Same cleanup as hide() but inline so we stay
    // .open across the loading → reading transition.
    this.el.classList.remove('reading');
    const slot = this.el.querySelector<HTMLSpanElement>('.bubble-reading');
    if (slot) slot.textContent = '';
    this.readingTextNode = null;
    // Track which translation key the caller is using so refreshLabels
    // can re-render the SR label if the UI language flips mid-loading.
    this.lastKey = label === this.tr('pillLoadingTranslate') ? 'translate' : 'speak';
    const labelEl = this.el.querySelector<HTMLSpanElement>('.bubble-label');
    if (labelEl) labelEl.textContent = label;
    // The .translating class is retained so CSS can opt to subtly
    // differentiate speak vs translate states (e.g. a different stem
    // pulse cadence) without changing the underlying glyph.
    this.el.classList.toggle('translating', this.lastKey === 'translate');
    this.el.classList.add('open');
  }

  hide(): void {
    this.el.classList.remove('open');
    this.el.classList.remove('reading');
    this.lastKey = null;
    // Reset reading slot for next call. The karaoke layer mutates this
    // element's children (wraps words in spans + restores on hide), so
    // clearing here is safe — if highlighter.hide() ran first it already
    // restored the original text node; if not, blowing away the slot
    // before next show() is the right cleanup.
    const slot = this.el.querySelector<HTMLSpanElement>('.bubble-reading');
    if (slot) slot.textContent = '';
    this.readingTextNode = null;
  }

  /**
   * Switch the bubble into reading mode for translated-audio playback.
   * The translated text is rendered as a single text node inside the
   * .bubble-reading slot. Returns a Range covering that text node so
   * the karaoke highlighter can wrap words inside the bubble (not the
   * user's original page selection, which is in a different language).
   *
   * Returns null if the DOM call fails (defensive — should never happen
   * in a browser-side widget context, but we have host pages with
   * aggressive observers that occasionally mutate detached elements).
   */
  showReading(text: string): Range | null {
    const slot = this.el.querySelector<HTMLSpanElement>('.bubble-reading');
    if (!slot) return null;
    // Clear any prior content (carryover from a previous translated
    // utterance whose highlighter didn't restore cleanly).
    slot.textContent = '';
    const node = document.createTextNode(text);
    slot.appendChild(node);
    this.readingTextNode = node;
    this.el.classList.add('reading');
    this.el.classList.add('open');
    try {
      const range = document.createRange();
      range.selectNodeContents(node);
      return range;
    } catch {
      return null;
    }
  }

  positionTo(rect: DOMRect): void {
    const top = Math.max(8, rect.top - 30);
    const left = Math.max(8, rect.left + rect.width / 2 - 16);
    this.el.style.top = `${top}px`;
    this.el.style.left = `${left}px`;
  }

  refreshLabels(tr: TranslateFn): void {
    this.tr = tr;
    if (this.lastKey) {
      const labelEl = this.el.querySelector<HTMLSpanElement>('.bubble-label');
      if (labelEl) {
        labelEl.textContent = this.lastKey === 'translate'
          ? tr('pillLoadingTranslate')
          : tr('pillLoadingSpeak');
      }
    }
  }
}
